import React, { useCallback, useState } from 'react';
import { TooltipProps } from '@utmost.co/utmost-design-system';
import { Tooltip } from '@beeline.com/bp-design-system';
import styled from 'styled-components';

interface TruncateTextProps {
  maxCharacters: 'parent' | number;
  children: string;
  dataTest?: string;
  overflowText?: string;
  wrap?: boolean;
  tooltip?: boolean;
  tooltipPosition?: TooltipProps['placement'];
}

const IS_TEST = process.env.NODE_ENV === 'test';
const TEST_WIDTH = 800;

export const TruncateText: React.FC<React.PropsWithChildren<TruncateTextProps>> = ({
  overflowText,
  maxCharacters,
  dataTest,
  children,
  tooltip = true,
  wrap = true,
  tooltipPosition = 'bottom'
}) => {
  const [truncatedText, setTruncatedText] = useState('');

  const isTruncated = truncatedText !== children;

  const handleRefChange = useCallback(
    (element: HTMLElement | null) => {
      const parent =
        maxCharacters === 'parent' && tooltip ? element?.parentElement?.parentElement : element?.parentElement;

      // getContentWidth(parent) / 6.5 is a max character ratio for current font
      let maxChars: number;
      if (typeof maxCharacters === 'number') {
        maxChars = maxCharacters;
      } else {
        maxChars = parent ? Math.floor(getContentWidth(parent) / 6) : 0;
      }

      if (!element) {
        setTruncatedText('');
      } else {
        setTruncatedText(truncate(children, maxChars, overflowText));
      }
    },
    [children, maxCharacters, overflowText, tooltip]
  );

  const text = (
    <span ref={handleRefChange} data-test={dataTest}>
      <StyledDiv>{truncatedText}</StyledDiv>
    </span>
  );

  if (!tooltip) {
    return <>{text}</>;
  }
  return (
    <Tooltip title={children} placement={tooltipPosition} disabled={!isTruncated}>
      {text}
    </Tooltip>
  );
};

function truncate(fullStr: string, strLen: number, overflowText = '...') {
  if (fullStr.length <= strLen) return fullStr;
  const sepLen = overflowText.length;
  const charsToShow = strLen - sepLen;
  return `${fullStr.substring(0, charsToShow)}${overflowText}`;
}

function getContentWidth(element?: HTMLElement | null): number {
  if (!element) {
    return 0;
  }
  const computed = element ? getComputedStyle(element) : { paddingLeft: '0', paddingRight: '0' };
  const paddings = parseFloat(computed.paddingLeft) + parseFloat(computed.paddingRight);

  return IS_TEST ? TEST_WIDTH : element.clientWidth - paddings;
}

const StyledDiv = styled.div`
  text-wrap-mode: nowrap;
`;
